import React from "react"
import { twMerge } from "tailwind-merge"

import AnimatedSection from "../../ProProfileShared/AnimatedSection"
import { applyButtonTheme, getButtonClasses } from "../utils/themeUtils"
import { DEFAULT_HIGHLIGHT_CLASSES } from "../WebsiteBuilderV2Page"

import HeaderSectionMobile from "./HeaderSectionMobile"

// Helper function to determine text color based on background
const getContrastingTextColor = (layout, hasBackgroundImage, theme, isInWhiteContainer = false) => {
  // If content is in a white container (like scattered_images with multiple images), use dark text
  if (isInWhiteContainer) {
    return theme?.colors?.textDark || "#000000"
  }
  // If there's a background image or it's a full image layout, use light text
  if (hasBackgroundImage || layout === "full_image" || layout.includes("image")) {
    return theme?.colors?.textLight || "#FFFFFF"
  }
  // Otherwise use dark text for light backgrounds
  return theme?.colors?.textDark || "#55433F"
}

const SubHeroSectionMobile = ({ section, practice, theme, highlightedField }) => {
  const isHighlighted = highlightedField === `section-${section.id}`
  const settings = section.settings || {}
  const layout = settings.layout || "full_image"
  const overlayOpacity = settings.overlayOpacity || 30
  const showCTA = settings.showCTA !== undefined ? settings.showCTA : true

  const ctaText = settings.ctaText || "Learn More"
  const ctaAction = settings.ctaAction || "page"
  const ctaTarget = settings.ctaTarget || ""

  const handleCTAClick = () => {
    const scrollToLayout = (layoutId) => {
      if (!layoutId) return
      const targetElement = document.querySelector(`[id^=section-${layoutId}-]`)
      if (targetElement) {
        targetElement.scrollIntoView({ behavior: "smooth" })
      }
    }

    switch (ctaAction) {
      case "page": {
        if (ctaTarget) {
          const { origin, pathname } = window.location
          const parts = pathname.split("/").filter(Boolean)
          if (parts.length >= 2 && parts[0] !== "portal") {
            const targetUrl = `${origin}/${parts[0]}/${ctaTarget}`
            window.location.href = targetUrl
          } else {
            window.location.href = `/${ctaTarget}`
          }
        }
        break
      }
      case "service":
        if (ctaTarget) {
          window.open(`/services/${ctaTarget}`, "_blank")
        } else {
          window.open("/services", "_blank")
        }
        break
      case "booking":
        window.open("/services", "_blank")
        break
      case "contact":
        scrollToLayout("contact")
        break
      case "scroll":
        window.scrollBy({ top: window.innerHeight, behavior: "smooth" })
        break
      case "external":
        if (ctaTarget) {
          const url = ctaTarget.startsWith("http") ? ctaTarget : `https://${ctaTarget}`
          window.open(url, "_blank")
        }
        break
      default:
        break
    }
  }

  const renderContent = (hasBackgroundImage = false, customLayout = null, isInWhiteContainer = false) => {
    const currentLayout = customLayout || layout
    const textColor = getContrastingTextColor(currentLayout, hasBackgroundImage, theme, isInWhiteContainer)

    return (
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-2 md:gap-3">
          {section.headline && (
            <h1
              className="w-full"
              style={{
                fontSize: settings.headlineSize || "36px",
                lineHeight: "40px",
                fontFamily: theme?.fonts?.heading || "Playfair Display, serif",
                fontWeight: theme?.fonts?.headingWeight || "700",
                color: settings.headlineColor || textColor,
                textAlign: settings.headingAlign || settings.textAlign || "left"
              }}>
              {section.headline}
            </h1>
          )}
          {section.subheadline && (
            <p
              className="w-full"
              style={{
                fontSize: settings.subheadlineSize || "16px",
                fontFamily: theme?.fonts?.paragraph || "Work Sans, sans-serif",
                fontWeight: theme?.fonts?.paragraphRegularWeight || "400",
                color: settings.subheadlineColor || textColor,
                textAlign: settings.subheadingAlign || settings.textAlign || "left"
              }}>
              {section.subheadline}
            </p>
          )}
        </div>

        <div
          className="flex flex-col gap-3 md:gap-4"
          style={{
            alignItems:
              settings.ctaAlign === "center" ? "center" : settings.ctaAlign === "right" ? "flex-end" : "flex-start",
            marginTop: settings.ctaTopMargin ? `${settings.ctaTopMargin}px` : "48px"
          }}>
          {showCTA && (
            <button
              type="button"
              onClick={handleCTAClick}
              className={twMerge(
                "inline-flex items-center justify-center px-8 py-2.5 text-[14px] font-normal leading-[20px] text-white transition-all hover:shadow-lg md:px-5 md:py-1.5 md:text-base",
                getButtonClasses(theme?.buttonStyle)
              )}
              style={applyButtonTheme(
                {
                  backgroundColor: theme?.colors?.primary || "#BD654F",
                  fontFamily: theme?.fonts?.paragraph || "Work Sans, sans-serif"
                },
                theme
              )}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = theme?.colors?.primaryDark || "#A55A45"
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = theme?.colors?.primary || "#BD654F"
              }}>
              {ctaText}
            </button>
          )}
        </div>
      </div>
    )
  }

  switch (layout) {
    case "image_left":
      return (
        <section
          className={twMerge(
            "relative flex items-center justify-start",
            isHighlighted ? DEFAULT_HIGHLIGHT_CLASSES : ""
          )}
          style={{
            backgroundColor: theme?.colors?.bgLight || "#FFFFFF"
          }}>
          <AnimatedSection className="mx-auto w-full px-5 py-8">
            <div className="flex flex-col items-center gap-5">
              {/* Mobile Image */}
              {section.imageUrl || settings.images?.[0]?.url ? (
                <div className="relative w-full" style={{ margin: "1em 0" }}>
                  <div
                    className="h-[320px] w-full rounded-lg bg-cover bg-center"
                    style={{ backgroundImage: `url(${section.imageUrl || settings.images?.[0]?.url})` }}
                  />
                </div>
              ) : (
                <div className="relative w-full" style={{ margin: "1em 0" }}>
                  <div className="flex h-[320px] w-full items-center justify-center rounded-lg bg-gray-200">
                    <span className="text-gray-500">No image selected</span>
                  </div>
                </div>
              )}
              <div className="w-full flex-1">{renderContent(false)}</div>
            </div>
          </AnimatedSection>
        </section>
      )

    case "image_right":
      return (
        <section
          className={twMerge(
            "relative flex items-center justify-start",
            isHighlighted ? DEFAULT_HIGHLIGHT_CLASSES : ""
          )}
          style={{
            backgroundColor: theme?.colors?.bgLight || "#FFFFFF"
          }}>
          <AnimatedSection className="mx-auto w-full px-5 py-8">
            <div className="flex flex-col items-center gap-5">
              {/* Mobile Image */}
              {section.imageUrl || settings.images?.[0]?.url ? (
                <div className="relative w-full" style={{ margin: "1em 0" }}>
                  <div
                    className="h-[320px] w-full rounded-lg bg-cover bg-center"
                    style={{ backgroundImage: `url(${section.imageUrl || settings.images?.[0]?.url})` }}
                  />
                </div>
              ) : (
                <div className="relative w-full" style={{ margin: "1em 0" }}>
                  <div className="flex h-[320px] w-full items-center justify-center rounded-lg bg-gray-200">
                    <span className="text-gray-500">No image selected</span>
                  </div>
                </div>
              )}
              <div className="w-full flex-1">{renderContent(false)}</div>
            </div>
          </AnimatedSection>
        </section>
      )

    case "half_image_left": {
      return (
        <section
          className={twMerge(
            "relative flex min-h-[300px] flex-col md:flex-row",
            isHighlighted ? DEFAULT_HIGHLIGHT_CLASSES : ""
          )}>
          <div className="relative h-[200px] md:order-1 md:h-auto md:flex-1">
            {(section.imageUrl || settings.images?.[0]?.url) && (
              <>
                <div
                  className="absolute inset-0 bg-cover bg-center"
                  style={{ backgroundImage: `url(${section.imageUrl || settings.images?.[0]?.url})` }}
                />
                <div
                  className="absolute inset-0"
                  style={{ backgroundColor: `rgba(0, 0, 0, ${overlayOpacity / 100})` }}
                />
              </>
            )}
          </div>
          <div
            className="flex items-center justify-center p-4 md:order-2 md:flex-1 md:px-10 md:py-16"
            style={{
              backgroundColor: theme?.colors?.bgLight || "#FFFFFF"
            }}>
            <AnimatedSection className="w-full max-w-lg">{renderContent(false)}</AnimatedSection>
          </div>
        </section>
      )
    }

    case "half_image_right": {
      return (
        <section
          className={twMerge(
            "relative flex min-h-[300px] flex-col md:flex-row",
            isHighlighted ? DEFAULT_HIGHLIGHT_CLASSES : ""
          )}>
          <div className="relative h-[200px] md:order-2 md:h-auto md:flex-1">
            {(section.imageUrl || settings.images?.[0]?.url) && (
              <>
                <div
                  className="absolute inset-0 bg-cover bg-center"
                  style={{ backgroundImage: `url(${section.imageUrl || settings.images?.[0]?.url})` }}
                />
                <div
                  className="absolute inset-0"
                  style={{ backgroundColor: `rgba(0, 0, 0, ${overlayOpacity / 100})` }}
                />
              </>
            )}
          </div>
          <div
            className="flex items-center justify-center p-4 md:order-1 md:flex-1 md:px-10 md:py-16"
            style={{
              backgroundColor: theme?.colors?.bgLight || "#FFFFFF"
            }}>
            <AnimatedSection className="w-full max-w-lg">{renderContent(false)}</AnimatedSection>
          </div>
        </section>
      )
    }

    case "scattered_images": {
      const images = section.settings?.images || []
      const imageCount = images.length

      // Case 1: Single image - use full image layout
      if (imageCount === 1) {
        return (
          <section
            className={twMerge(
              "relative flex items-center justify-center py-12 md:py-16",
              isHighlighted ? DEFAULT_HIGHLIGHT_CLASSES : ""
            )}
            style={{
              backgroundColor: theme?.colors?.bgLight || "#FFFFFF",
              minHeight: "300px"
            }}>
            {images[0]?.url && (
              <div
                className="absolute inset-0 bg-cover bg-center"
                style={{ backgroundImage: `url(${images[0].url})` }}
              />
            )}
            <div className="absolute inset-0" style={{ backgroundColor: `rgba(0, 0, 0, ${overlayOpacity / 100})` }} />
            <AnimatedSection className="relative z-10 mx-auto max-w-4xl px-4 py-8 md:px-4">
              {renderContent(true, "full_image")}
            </AnimatedSection>
          </section>
        )
      }

      // Case 2: Two images - overlapped layout with centered content (based on Figma)
      if (imageCount === 2) {
        return (
          <section
            className={twMerge(
              "relative flex items-center justify-center py-6 md:py-16",
              isHighlighted ? DEFAULT_HIGHLIGHT_CLASSES : ""
            )}
            style={{
              backgroundColor: theme?.colors?.bgLight || "#FFFFFF",
              minHeight: "400px"
            }}>
            <div className="relative mx-auto w-full max-w-[1200px] px-4 md:px-0">
              {/* Mobile: Stack images above content */}
              <div className="flex flex-col gap-4 md:hidden">
                <div
                  className="h-[150px] w-full rounded-lg bg-cover bg-center"
                  style={{ backgroundImage: `url(${images[0]?.url})` }}
                />
                <div
                  className="h-[150px] w-full rounded-lg bg-cover bg-center"
                  style={{ backgroundImage: `url(${images[1]?.url})` }}
                />
              </div>

              {/* Desktop: Overlapped layout */}
              <div className="hidden md:block">
                {/* First image - left side, more centered */}
                <div
                  className="absolute h-[300px] w-[280px] rounded-lg bg-cover bg-center"
                  style={{
                    left: "80px",
                    top: "50px",
                    backgroundImage: `url(${images[0]?.url})`
                  }}
                />
                {/* Second image - right side, vertically centered with content */}
                <div
                  className="absolute right-[60px] top-1/2 h-[280px] w-[320px] -translate-y-1/2 rounded-lg bg-cover bg-center"
                  style={{
                    backgroundImage: `url(${images[1]?.url})`
                  }}
                />
              </div>

              {/* Content container */}
              <div
                className="relative z-10 w-full bg-white p-4 text-center md:absolute md:left-1/2 md:top-1/2 md:w-[420px] md:-translate-x-1/2 md:-translate-y-1/2 md:px-12 md:py-10"
                style={{
                  boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
                  minHeight: "auto"
                }}>
                <AnimatedSection>{renderContent(false, null, true)}</AnimatedSection>
              </div>
            </div>
          </section>
        )
      }

      // Case 3: Three+ images - scattered artistic layout (based on Figma)
      return (
        <section
          className={twMerge(
            "relative flex items-center justify-center py-6 md:py-16",
            isHighlighted ? DEFAULT_HIGHLIGHT_CLASSES : ""
          )}
          style={{
            backgroundColor: theme?.colors?.bgLight || "#FFFFFF",
            minHeight: "400px"
          }}>
          <div className="relative mx-auto w-full max-w-[1200px] px-4 md:px-0">
            {/* Mobile: Scattered layout with slight rotations */}
            <div className="flex flex-col gap-4 md:hidden">
              {images.slice(0, 3).map((image, index) => (
                <div
                  key={index}
                  className="h-[150px] w-full rounded-lg bg-cover bg-center shadow-lg"
                  style={{
                    backgroundImage: `url(${image?.url})`,
                    transform: `rotate(${index % 2 === 0 ? "-2deg" : "1deg"})`
                  }}
                />
              ))}
            </div>

            {/* Desktop: Scattered layout - 3 images scattered around centered text block */}
            <div className="hidden md:block">
              {/* First image - top left, slightly rotated */}
              {images.length > 0 && (
                <div
                  className="absolute h-[200px] w-[250px] rounded-lg bg-cover bg-center shadow-lg"
                  style={{
                    left: "40px",
                    top: "60px",
                    backgroundImage: `url(${images[0]?.url})`,
                    transform: "rotate(-8deg)"
                  }}
                />
              )}

              {/* Second image - bottom left, slightly rotated */}
              {images.length > 1 && (
                <div
                  className="absolute h-[180px] w-[220px] rounded-lg bg-cover bg-center shadow-lg"
                  style={{
                    left: "80px",
                    bottom: "80px",
                    backgroundImage: `url(${images[1]?.url})`,
                    transform: "rotate(5deg)"
                  }}
                />
              )}

              {/* Third image - top right, slightly rotated */}
              {images.length > 2 && (
                <div
                  className="absolute h-[240px] w-[280px] rounded-lg bg-cover bg-center shadow-lg"
                  style={{
                    right: "60px",
                    top: "40px",
                    backgroundImage: `url(${images[2]?.url})`,
                    transform: "rotate(-3deg)"
                  }}
                />
              )}

              {/* Content - centered with white background */}
              <div
                className="relative z-10 mx-auto w-[420px] bg-white px-12 py-10 text-center"
                style={{
                  boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
                  minHeight: "auto"
                }}>
                <AnimatedSection>{renderContent(false, null, true)}</AnimatedSection>
              </div>
            </div>
          </div>
        </section>
      )
    }

    case "overlap_double_image": {
      const images = section.settings?.images || []
      // Two images overlapped with centered content
      if (images.length >= 2) {
        return (
          <section
            className={twMerge(
              "relative flex items-center justify-center py-6 md:py-16",
              isHighlighted ? DEFAULT_HIGHLIGHT_CLASSES : ""
            )}
            style={{ backgroundColor: theme?.colors?.bgLight || "#FFFFFF", minHeight: "400px" }}>
            <div className="relative mx-auto w-full max-w-[1200px] px-4 md:px-0">
              {/* Mobile: Stack images above content */}
              <div className="flex flex-col gap-4 md:hidden">
                <div
                  className="h-[150px] w-full rounded-lg bg-cover bg-center"
                  style={{ backgroundImage: `url(${images[0]?.url})` }}
                />
                <div
                  className="h-[150px] w-full rounded-lg bg-cover bg-center"
                  style={{ backgroundImage: `url(${images[1]?.url})` }}
                />
              </div>

              {/* Desktop: Overlap double image - centered text with images on left and right */}
              <div className="hidden md:block">
                {/* Left image - positioned on the left side */}
                <div
                  className="absolute h-[280px] w-[250px] rounded-lg bg-cover bg-center shadow-lg"
                  style={{
                    left: "60px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    backgroundImage: `url(${images[0]?.url})`,
                    zIndex: 1
                  }}
                />
                {/* Right image - positioned on the right side */}
                <div
                  className="absolute h-[280px] w-[250px] rounded-lg bg-cover bg-center shadow-lg"
                  style={{
                    right: "60px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    backgroundImage: `url(${images[1]?.url})`,
                    zIndex: 1
                  }}
                />
              </div>

              {/* Content container - centered with higher z-index */}
              <div
                className="relative z-20 w-full bg-white p-4 text-center md:absolute md:left-1/2 md:top-1/2 md:w-[400px] md:-translate-x-1/2 md:-translate-y-1/2 md:p-12"
                style={{ boxShadow: "0 4px 20px rgba(0,0,0,0.1)", minHeight: "auto" }}>
                <AnimatedSection>{renderContent(false, null, true)}</AnimatedSection>
              </div>
            </div>
          </section>
        )
      }
      // One image: fall back to full image style with overlay
      if (images.length === 1) {
        return (
          <section
            className={twMerge(
              "relative flex items-center justify-center py-12 md:py-16",
              isHighlighted ? DEFAULT_HIGHLIGHT_CLASSES : ""
            )}
            style={{ backgroundColor: theme?.colors?.primary || "#3B82F6", minHeight: "300px" }}>
            {images[0]?.url && (
              <div
                className="absolute inset-0 bg-cover bg-center"
                style={{ backgroundImage: `url(${images[0].url})` }}
              />
            )}
            <div className="absolute inset-0" style={{ backgroundColor: `rgba(0, 0, 0, ${overlayOpacity / 100})` }} />
            <AnimatedSection className="relative z-10 mx-auto max-w-4xl px-4 py-8 md:px-4">
              {renderContent(true, "full_image")}
            </AnimatedSection>
          </section>
        )
      }
      // No images: content only
      return (
        <section
          className={twMerge(
            "relative flex items-center justify-center py-8",
            isHighlighted ? DEFAULT_HIGHLIGHT_CLASSES : ""
          )}
          style={{ backgroundColor: theme?.colors?.bgLight || "#FFFFFF", minHeight: "300px" }}>
          <AnimatedSection className="mx-auto max-w-4xl px-4 py-8 md:px-4">
            {renderContent(false, null, true)}
          </AnimatedSection>
        </section>
      )
    }

    case "contained_image":
      return (
        <section
          className={twMerge(
            "relative flex items-center justify-center p-8",
            isHighlighted ? DEFAULT_HIGHLIGHT_CLASSES : ""
          )}
          style={{ backgroundColor: theme?.colors?.bgLight || "#FFFFFF", minHeight: "300px" }}>
          {/* Image container with rounded corners */}
          <div className="absolute inset-8 overflow-hidden rounded-lg">
            {(section.imageUrl || settings.images?.[0]?.url) && (
              <div
                className="absolute inset-0 bg-cover bg-center"
                style={{ backgroundImage: `url(${section.imageUrl || settings.images?.[0]?.url})` }}
              />
            )}
            <div className="absolute inset-0" style={{ backgroundColor: `rgba(0, 0, 0, ${overlayOpacity / 100})` }} />
          </div>

          {/* Content overlaid on the image */}
          <AnimatedSection className="relative z-10 mx-auto max-w-4xl px-4 py-8 md:px-4">
            {renderContent(true, "contained_image")}
          </AnimatedSection>
        </section>
      )

    case "full_image":
    default:
      return (
        <section
          className={twMerge(
            "relative flex items-center justify-center py-12 md:py-16",
            isHighlighted ? DEFAULT_HIGHLIGHT_CLASSES : ""
          )}
          style={{ backgroundColor: theme?.colors?.primary || "#3B82F6", minHeight: "300px" }}>
          {(section.imageUrl || settings.images?.[0]?.url) && (
            <div
              className="absolute inset-0 bg-cover bg-center"
              style={{ backgroundImage: `url(${section.imageUrl || settings.images?.[0]?.url})` }}
            />
          )}
          <div className="absolute inset-0" style={{ backgroundColor: `rgba(0, 0, 0, ${overlayOpacity / 100})` }} />

          {/* Header superpuesto para full_image */}
          <div className="absolute inset-x-0 top-0 z-20">
            <HeaderSectionMobile
              section={{ id: "subhero-header", settings: { showPromoBanner: false } }}
              practice={practice}
              theme={theme}
              highlightedField={null}
              transparent={true}
            />
          </div>

          <AnimatedSection className="relative z-10 mx-auto max-w-4xl px-4 py-8 md:px-4">
            {renderContent(true, "full_image")}
          </AnimatedSection>
        </section>
      )
  }
}

export default SubHeroSectionMobile
